import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Banknote, RefreshCw, Loader2, Search, ExternalLink, Megaphone,
  TrendingUp, Wallet, ArrowDownRight, Users2, ShieldAlert,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as ChartTooltip } from "recharts";
import api, { apiError } from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { Loader, EmptyState } from "@/components/app/Loader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const usd = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function OpponentMoney() {
  const { hasRole } = useAuth();
  const qc = useQueryClient();
  const canEdit = hasRole("admin", "manager");

  const { data, isLoading } = useQuery({
    queryKey: ["moneySnapshot"],
    queryFn: () => api.get("/money/snapshot").then((r) => r.data),
  });

  const sync = useMutation({
    mutationFn: () => api.post("/money/sync").then((r) => r.data),
    onSuccess: (d) => {
      toast.success(
        `${d.new_feed_items} new money signal${d.new_feed_items === 1 ? "" : "s"} · ` +
        `${d.fec_signals} FEC filings · ${d.ad_signals} ads`
      );
      if (d.skipped?.length) toast.info(d.skipped.join(" · "));
      if (d.errors?.length) toast.error(d.errors.join(" · "));
      qc.invalidateQueries({ queryKey: ["moneySnapshot"] });
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const totals = (data?.committees || []).reduce(
    (acc, c) => ({
      receipts: acc.receipts + (c.receipts || 0),
      disbursements: acc.disbursements + (c.disbursements || 0),
      cash_on_hand: acc.cash_on_hand + (c.cash_on_hand || 0),
      individual: acc.individual + (c.individual_contributions || 0),
    }),
    { receipts: 0, disbursements: 0, cash_on_hand: 0, individual: 0 }
  );

  return (
    <div data-testid="money-page">
      <PageHeader
        title="Opponent Money Tracker"
        subtitle="FEC filings and Meta Ad Library launches, pulled automatically into the listening feed, plus a head-to-head cash comparison, so new fundraising and new ads never surprise you."
        icon={Banknote}
        actions={
          canEdit && (
            <Button
              data-testid="money-sync-btn"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              className="bg-brand-red text-white hover:bg-brand-redhover"
            >
              {sync.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Sync money signals
            </Button>
          )
        }
      />

      {isLoading ? (
        <Loader label="Loading opponent finance…" />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-white p-4 shadow-sm">
            <span className="font-heading text-sm font-bold text-brand-ink" data-testid="money-opponent-name">
              {data?.opponent || "No opponent set"}
            </span>
            <Badge variant="outline" className="border-brand-navy/20 bg-brand-navy/[0.04] text-brand-navy">
              {data?.office === "H" ? `MI-${data?.district || "04"} · ` : ""}{data?.cycle} cycle
            </Badge>
            {(data?.committee_ids || []).map((c) => (
              <Badge key={c} variant="outline" className="font-mono text-[0.7rem]">{c}</Badge>
            ))}
            <Pill ok={data?.fec_connected} label="OpenFEC" testId="money-fec-status" />
            <Pill ok={data?.meta_connected} label="Meta Ad Library" testId="money-meta-status" />
            <span className="ml-auto text-xs text-brand-mute">
              {data?.last_sync_at ? `Synced ${timeAgo(data.last_sync_at)}` : "Never synced"}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={TrendingUp} label="Raised this cycle" value={usd(totals.receipts)} tone="red" testId="kpi-raised" />
            <Kpi icon={Wallet} label="Cash on hand" value={usd(totals.cash_on_hand)} tone="navy" testId="kpi-coh" />
            <Kpi icon={ArrowDownRight} label="Spent" value={usd(totals.disbursements)} tone="slate" testId="kpi-spent" />
            <Kpi icon={Users2} label="From individuals" value={usd(totals.individual)} tone="slate" testId="kpi-individual" />
          </div>

          {data?.head_to_head?.own_name && (data.head_to_head.own_raised > 0 || data.head_to_head.opponent_raised > 0) && (
            <HeadToHead h2h={data.head_to_head} />
          )}

          {data?.series?.length > 1 && (
            <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="money-trend-card">
              <h3 className="font-heading text-sm font-bold text-brand-ink">Fundraising trend (per sync)</h3>
              <div className="mt-3 h-44 min-h-[176px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={170}>
                  <AreaChart data={data.series} margin={{ top: 4, right: 8, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="mgrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E63946" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#E63946" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6C757D" }} axisLine={false} tickLine={false} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: "#6C757D" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <ChartTooltip formatter={(v) => usd(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Area type="monotone" dataKey="raised" name="Raised" stroke="#E63946" fill="url(#mgrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="cash_on_hand" name="Cash on hand" stroke="#1D3557" fill="transparent" strokeWidth={2} />
                    <Area type="monotone" dataKey="own_cash_on_hand" name="Our cash on hand" stroke="#2A9D8F" fill="transparent" strokeWidth={2} strokeDasharray="4 3" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {canEdit && <TargetConfig data={data} />}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <AdsPanel ads={data?.ads || []} connected={data?.meta_connected} />
            <SignalsPanel signals={data?.signals || []} />
          </div>
        </div>
      )}
    </div>
  );
}

function HeadToHead({ h2h }) {
  const rows = [
    { key: "raised", label: "Raised this cycle", own: h2h.own_raised, opp: h2h.opponent_raised },
    { key: "coh", label: "Cash on hand", own: h2h.own_cash_on_hand, opp: h2h.opponent_cash_on_hand },
    { key: "spent", label: "Spent", own: h2h.own_spent, opp: h2h.opponent_spent },
  ];
  const behind = h2h.coh_gap > 0;
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="money-head-to-head">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-heading text-sm font-bold text-brand-ink">The money race</h3>
        <span className="text-xs text-brand-mute">
          {h2h.own_name} vs {h2h.opponent_name}, FEC cycle totals
        </span>
        <span
          className={`ml-auto rounded-full px-2.5 py-1 text-xs font-bold ${
            behind ? "bg-neg-bg text-neg-text" : "bg-pos-bg text-pos-text"
          }`}
          data-testid="money-coh-gap"
        >
          {behind
            ? `${usd(h2h.coh_gap)} cash-on-hand deficit`
            : `${usd(Math.abs(h2h.coh_gap))} cash-on-hand advantage`}
        </span>
      </div>

      <div className="mt-4 space-y-4">
        {rows.map((r) => {
          const max = Math.max(r.own, r.opp, 1);
          return (
            <div key={r.key} data-testid={`h2h-row-${r.key}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-semibold text-brand-ink">{r.label}</span>
                <span className="text-brand-mute">
                  <strong className="text-teal-700">{usd(r.own)}</strong> vs{" "}
                  <strong className="text-brand-red">{usd(r.opp)}</strong>
                </span>
              </div>
              <div className="mt-1.5 space-y-1">
                <Bar value={r.own} max={max} tone="bg-teal-600" label={h2h.own_name} />
                <Bar value={r.opp} max={max} tone="bg-brand-red" label={h2h.opponent_name} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-[0.7rem] text-brand-mute">
        Incumbent war chests build over multiple cycles. Treat the gap as the fundraising target,
        not a verdict. Numbers update on every sync straight from FEC filings.
      </p>
    </div>
  );
}

function Bar({ value, max, tone, label }) {
  const pct = Math.max(2, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 truncate text-[0.7rem] text-brand-mute">{label}</span>
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${tone} transition-[width] duration-700 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Pill({ ok, label, testId }) {
  return (
    <span
      data-testid={testId}
      className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide ${
        ok ? "bg-pos-bg text-pos-text" : "bg-slate-100 text-slate-500"
      }`}
    >
      {label} {ok ? "live" : "off"}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, tone, testId }) {
  const tones = {
    red: "bg-brand-red/10 text-brand-red",
    navy: "bg-brand-navy/10 text-brand-navy",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-brand-ink">{value}</p>
      <p className="mt-0.5 text-xs font-medium text-brand-mute">{label}</p>
    </div>
  );
}

function TargetConfig({ data }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (name.trim().length < 2) return toast.error("Enter at least 2 characters");
    setBusy(true);
    try {
      const { data: r } = await api.get("/money/candidate-search", { params: { name: name.trim() } });
      setResults(r.results || []);
      if (!r.results?.length) toast.info("No FEC candidates matched in Michigan");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const pick = async (cand, committeeId) => {
    setBusy(true);
    try {
      await api.put("/money/config", {
        opponent_name: cand.name, committee_ids: [committeeId], ad_search_terms: [cand.name],
      });
      toast.success(`Tracking ${cand.name} (${committeeId})`);
      setResults(null);
      setName("");
      qc.invalidateQueries({ queryKey: ["moneySnapshot"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="money-target-card">
      <h3 className="font-heading text-sm font-bold text-brand-ink">Who are we tracking?</h3>
      <p className="mt-1 text-xs text-brand-mute">
        Search the FEC by name to lock onto the right committee. Names aren't unique, so pick the
        exact filer. Currently tracking <strong>{data?.opponent || "nobody"}</strong>.
      </p>
      <div className="mt-3 flex gap-2">
        <Label htmlFor="fec-name" className="sr-only">Opponent name</Label>
        <Input
          id="fec-name"
          data-testid="fec-search-input"
          placeholder="Candidate name (e.g. Huizenga)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <Button data-testid="fec-search-btn" onClick={search} disabled={busy} variant="outline">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {results?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="fec-search-results">
          {results.map((c) => (
            <div key={c.candidate_id} className="rounded-lg border border-border p-3">
              <p className="text-sm font-bold text-brand-ink">{c.name}</p>
              <p className="text-[0.7rem] text-brand-mute">
                {c.office} · {c.state} · {c.party} · {c.incumbent}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {c.committees.length === 0 && (
                  <span className="text-[0.7rem] text-brand-mute">No principal committee on file</span>
                )}
                {c.committees.map((m) => (
                  <Button
                    key={m.committee_id}
                    size="sm"
                    variant="outline"
                    data-testid={`fec-pick-${m.committee_id}`}
                    onClick={() => pick(c, m.committee_id)}
                    disabled={busy}
                    className="text-xs"
                  >
                    Track {m.committee_id}, {m.name}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdsPanel({ ads, connected }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="money-ads-card">
      <div className="flex items-center gap-2">
        <Megaphone className="h-4 w-4 text-brand-red" />
        <h3 className="font-heading text-sm font-bold text-brand-ink">Opponent ads (Meta Ad Library)</h3>
      </div>
      {!connected ? (
        <div className="mt-4 rounded-lg border border-dashed border-border p-4" data-testid="money-ads-not-connected">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-semibold text-brand-ink">Meta access token not connected</p>
              <p className="mt-1 text-xs text-brand-mute">
                The Ad Library API requires a Meta developer app plus ID + location confirmation at
                facebook.com/ID (approval can take several days). Once you have a token, add it under
                Settings → Integrations as <strong>Meta Ad Library</strong> and ads start landing in the
                feed automatically.
              </p>
            </div>
          </div>
        </div>
      ) : !ads.length ? (
        <p className="mt-4 text-xs text-brand-mute">No ads found in the current lookback window.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {ads.slice(0, 8).map((a) => (
            <div key={a.url} className="rounded-lg border border-border p-3">
              <p className="text-sm font-semibold text-brand-ink">{a.title}</p>
              <p className="mt-1 text-xs text-brand-mute">{a.text}</p>
              <a href={a.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline">
                Archived ad <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SignalsPanel({ signals }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm" data-testid="money-signals-card">
      <h3 className="font-heading text-sm font-bold text-brand-ink">Recent money signals in the feed</h3>
      {!signals.length ? (
        <div className="mt-3">
          <EmptyState icon={Banknote} title="No money signals yet" description="Run a sync to pull the latest FEC filings and ad launches." />
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {signals.slice(0, 10).map((s) => (
            <div key={s.id} className="rounded-lg border border-border p-3" data-testid={`money-signal-${s.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-brand-red/25 bg-brand-red/[0.05] text-[0.65rem] uppercase text-brand-red">
                  {s.source_type === "fec" ? "FEC" : "Meta ads"}
                </Badge>
                <span className="text-[0.7rem] text-brand-mute">{timeAgo(s.timestamp)}</span>
                {s.flag === "threat" && (
                  <span className="rounded-full bg-neg-bg px-2 py-0.5 text-[0.6rem] font-bold uppercase text-neg-text">Threat</span>
                )}
              </div>
              <p className="mt-1 text-sm font-semibold text-brand-ink">{s.title}</p>
              <p className="mt-0.5 text-xs text-brand-mute">{s.text}</p>
              <a href={s.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-blue hover:underline">
                Source <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
